import { Clock, FileSignature, Stamp } from 'lucide-react';
import { ReunionTransaction } from '../../../hooks/useReunionMensuelleData';
import { PeriodBucket, diffDays, getPeriodKeyForDate, buildPeriodColumnAverages } from '../../../utils/reunionMensuelle';
import { Scope, ScopeColumn, getColumnForTransaction } from './ScopeSelector';
import TrendChartCard from './TrendChartCard';

interface DelaiVenteTabProps {
  transactions: ReunionTransaction[];
  periods: PeriodBucket[];
  columns: ScopeColumn[];
  scope: Scope;
}

export default function DelaiVenteTab({ transactions, periods, columns, scope }: DelaiVenteTabProps) {
  const columnKeys = columns.map(c => c.key);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);
  const getColumnKey = (tx: ReunionTransaction) => getColumnForTransaction(tx, scope);

  const mandatToCompromis = buildPeriodColumnAverages(
    transactions, periods, columnKeys, getPeriodKey, getColumnKey,
    tx => diffDays(tx.compromise_date, tx.mandate_date)
  );
  const compromisToActePrevi = buildPeriodColumnAverages(
    transactions, periods, columnKeys, getPeriodKey, getColumnKey,
    tx => diffDays(tx.expected_acte_date, tx.compromise_date)
  );
  const compromisToActeSigne = buildPeriodColumnAverages(
    transactions, periods, columnKeys, getPeriodKey, getColumnKey,
    tx => diffDays(tx.acte_date, tx.compromise_date)
  );

  const formatDays = (v: number) => Math.round(v).toString();

  const makeGetTransactions = (getDelay: (tx: ReunionTransaction) => number | null) =>
    (periodKey: string, columnKey: string) =>
      transactions.filter(tx =>
        getPeriodKey(tx) === periodKey && getColumnKey(tx) === columnKey && getDelay(tx) !== null
      );

  return (
    <div className="space-y-5">
      <TrendChartCard
        icon={<FileSignature size={16} className="text-indigo-600" />}
        iconBg="bg-indigo-50"
        title="Mandat → Compromis"
        periods={periods}
        columns={columns}
        data={mandatToCompromis}
        valueFormatter={formatDays}
        valueSuffix=" j"
        getTransactions={makeGetTransactions(tx => diffDays(tx.compromise_date, tx.mandate_date))}
        getMetricValue={tx => diffDays(tx.compromise_date, tx.mandate_date)}
        metricLabel="Délai"
        infoText="Chaque vente est rangée dans le mois/trimestre de sa date de compromis. Pour les ventes du mois ayant une date de mandat renseignée, on calcule (date de compromis − date de mandat), puis on fait la moyenne de ces délais."
      />
      <TrendChartCard
        icon={<Clock size={16} className="text-amber-600" />}
        iconBg="bg-amber-50"
        title="Compromis → Acte prévisionnel"
        periods={periods}
        columns={columns}
        data={compromisToActePrevi}
        valueFormatter={formatDays}
        valueSuffix=" j"
        getTransactions={makeGetTransactions(tx => diffDays(tx.expected_acte_date, tx.compromise_date))}
        getMetricValue={tx => diffDays(tx.expected_acte_date, tx.compromise_date)}
        metricLabel="Délai"
        infoText="Chaque vente est rangée dans le mois/trimestre de sa date de compromis. On calcule (date d'acte prévisionnelle − date de compromis) pour les ventes du mois ayant une date d'acte prévisionnelle, puis on fait la moyenne."
      />
      <TrendChartCard
        icon={<Stamp size={16} className="text-emerald-600" />}
        iconBg="bg-emerald-50"
        title="Compromis → Acte signé"
        periods={periods}
        columns={columns}
        data={compromisToActeSigne}
        valueFormatter={formatDays}
        valueSuffix=" j"
        getTransactions={makeGetTransactions(tx => diffDays(tx.acte_date, tx.compromise_date))}
        getMetricValue={tx => diffDays(tx.acte_date, tx.compromise_date)}
        metricLabel="Délai"
        infoText="Chaque vente est rangée dans le mois/trimestre de sa date de compromis. On calcule (date d'acte signé − date de compromis) pour les ventes du mois déjà actées, puis on fait la moyenne."
      />
    </div>
  );
}
